import { useEffect, useState } from "react";
import { Inbox, Loader2, Send, FileText, Filter, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminOutboxAPI, extractError } from "@/lib/api";
import { cn } from "@/lib/utils";

const CHANNELS = ["__any__", "whatsapp", "email", "sms", "in_app"];
const STATUSES = ["__any__", "queued", "sent", "failed"];
const SOURCE_TYPES = ["__any__", "service_reminder", "daily_upsell_digest", "dvi_followup"];

function StatusPill({ status }) {
    const palette = {
        queued:
            "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-300",
        sent: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
        failed:
            "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300",
    };
    return (
        <span
            data-testid={`outbox-status-${status}`}
            className={cn(
                "inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider",
                palette[status] || palette.queued,
            )}
        >
            {status}
        </span>
    );
}

function ChannelChip({ channel }) {
    return (
        <span className="rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
            {channel}
        </span>
    );
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
}

export default function AdminOutboxPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [channel, setChannel] = useState("__any__");
    const [status, setStatus] = useState("__any__");
    const [sourceType, setSourceType] = useState("__any__");
    const [tenantId, setTenantId] = useState("");
    const [page, setPage] = useState(1);
    const [drillOpen, setDrillOpen] = useState(false);
    const [drill, setDrill] = useState(null);
    const PAGE_SIZE = 50;

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const params = { page, page_size: PAGE_SIZE };
        if (channel && channel !== "__any__") params.channel = channel;
        if (status && status !== "__any__") params.status = status;
        if (sourceType && sourceType !== "__any__") params.source_type = sourceType;
        if (tenantId.trim()) params.tenant_id = tenantId.trim();
        AdminOutboxAPI.list(params)
            .then((d) => {
                if (!alive) return;
                setItems(d.items || []);
                setTotal(d.total || 0);
            })
            .catch((e) => {
                if (!alive) return;
                toast.error(extractError(e));
                setItems([]);
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [channel, status, sourceType, tenantId, page]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-6" data-testid="admin-outbox-page">
            <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        Platform · Engagement
                    </div>
                    <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
                        <Send className="h-6 w-6 text-blue-500" />
                        Notification outbox
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-zinc-500">
                        Every customer-facing message AutoBrix would have sent. Provider is{" "}
                        <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">stub</code>{" "}
                        in this build — real Twilio / WhatsApp / SES providers can be wired in later.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1 text-right">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Total messages
                    </div>
                    <div
                        className="font-display text-2xl font-bold tabular-nums"
                        data-testid="admin-outbox-total"
                    >
                        {total}
                    </div>
                </div>
            </header>

            <Card className="flex flex-wrap items-end gap-3 p-3">
                <div className="flex flex-col gap-1">
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Status
                    </label>
                    <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
                        <SelectTrigger className="w-32" data-testid="admin-outbox-filter-status">
                            <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s === "__any__" ? "Any" : s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Channel
                    </label>
                    <Select value={channel} onValueChange={(v) => { setPage(1); setChannel(v); }}>
                        <SelectTrigger className="w-32" data-testid="admin-outbox-filter-channel">
                            <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                            {CHANNELS.map((c) => (
                                <SelectItem key={c} value={c}>
                                    {c === "__any__" ? "Any" : c}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Source
                    </label>
                    <Select value={sourceType} onValueChange={(v) => { setPage(1); setSourceType(v); }}>
                        <SelectTrigger className="w-40" data-testid="admin-outbox-filter-source">
                            <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                            {SOURCE_TYPES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s === "__any__" ? "Any" : s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-1 flex-col gap-1">
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Tenant ID
                    </label>
                    <Input
                        data-testid="admin-outbox-filter-tenant"
                        placeholder="Filter by tenant id (paste an ObjectId)"
                        value={tenantId}
                        onChange={(e) => { setPage(1); setTenantId(e.target.value); }}
                        className="w-full max-w-md"
                    />
                </div>
            </Card>

            <Card className="overflow-hidden" data-testid="admin-outbox-table-card">
                {loading ? (
                    <div className="flex h-40 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                        <Inbox className="h-8 w-8 text-zinc-400" />
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            No outbox messages match this view
                        </div>
                    </div>
                ) : (
                    <table className="w-full table-auto text-sm">
                        <thead className="border-b border-zinc-200 bg-zinc-50/60 text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
                            <tr>
                                <th className="px-3 py-2.5">Created</th>
                                <th className="px-3 py-2.5">Tenant</th>
                                <th className="px-3 py-2.5">Channel</th>
                                <th className="px-3 py-2.5">Source</th>
                                <th className="px-3 py-2.5">Subject (preview)</th>
                                <th className="px-3 py-2.5">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                            {items.map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`outbox-row-${r.id}`}
                                    onClick={() => {
                                        setDrill(r);
                                        setDrillOpen(true);
                                    }}
                                    className="cursor-pointer transition-colors hover:bg-zinc-50/60 dark:hover:bg-zinc-900/30"
                                >
                                    <td className="px-3 py-2.5 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                                        {fmt(r.created_at)}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-1 text-xs">
                                            <Building2 className="h-3 w-3 text-zinc-400" />
                                            <span className="truncate">
                                                {r.tenant_name || r.tenant_id?.slice(-8) || "—"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <ChannelChip channel={r.channel} />
                                    </td>
                                    <td className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                        {r.source_type}
                                    </td>
                                    <td className="px-3 py-2.5 max-w-md truncate text-zinc-700 dark:text-zinc-300">
                                        {r.rendered_subject || r.template_key}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <StatusPill status={r.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-zinc-100 px-3 py-2 dark:border-zinc-800">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            Page {page} / {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded border border-zinc-300 px-2 py-1 text-xs disabled:opacity-50 dark:border-zinc-700"
                            >
                                ‹ Prev
                            </button>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                data-testid="admin-outbox-next"
                                className="rounded border border-zinc-300 px-2 py-1 text-xs disabled:opacity-50 dark:border-zinc-700"
                            >
                                Next ›
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
                <DialogContent className="max-w-2xl" data-testid="admin-outbox-drill">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            {drill?.rendered_subject || drill?.template_key}
                        </DialogTitle>
                        <DialogDescription>
                            {drill?.tenant_name} · {drill?.channel} · {drill?.source_type}
                        </DialogDescription>
                    </DialogHeader>
                    {drill && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                        Recipient
                                    </div>
                                    <pre className="mt-1 max-h-32 overflow-auto rounded border border-zinc-200 bg-zinc-50 p-2 text-[11px] dark:border-zinc-800 dark:bg-zinc-900">
                                        {JSON.stringify(drill.recipient, null, 2)}
                                    </pre>
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                        Provider
                                    </div>
                                    <div className="mt-1 rounded border border-zinc-200 bg-zinc-50 p-2 font-mono text-xs dark:border-zinc-800 dark:bg-zinc-900">
                                        {drill.provider || "stub"} · retries={drill.retries}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    Body
                                </div>
                                <pre
                                    data-testid="admin-outbox-body"
                                    className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded border border-zinc-200 bg-zinc-50 p-3 text-[12px] leading-relaxed dark:border-zinc-800 dark:bg-zinc-900"
                                >
                                    {drill.rendered_body}
                                </pre>
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    Template payload
                                </div>
                                <pre className="mt-1 max-h-40 overflow-auto rounded border border-zinc-200 bg-zinc-50 p-2 text-[11px] dark:border-zinc-800 dark:bg-zinc-900">
                                    {JSON.stringify(drill.template_payload, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
